import React, { useMemo, useState } from 'react';
import {
  Presentation, PenTool, FileText, BarChart3, Table2, Search, Edit3,
  Trash2, Clock, Plus, Users, LogOut, Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import ProjectShareModal from './ProjectShareModal';

// One card per row of the `projects` table — every generation type, newest
// first (App.js maps and sorts them before passing the array down).
const TYPE_META = {
  'create-ppt': { label: 'Presentation', plural: 'Presentations', icon: Presentation, color: 'text-green-500' },
  presentation: { label: 'Presentation', plural: 'Presentations', icon: Presentation, color: 'text-green-500' },
  redesign: { label: 'Redesign', plural: 'Redesigns', icon: Edit3, color: 'text-pink-500' },
  infographic: { label: 'Infographic', plural: 'Infographics', icon: PenTool, color: 'text-purple-500' },
  document: { label: 'Document', plural: 'Documents', icon: FileText, color: 'text-blue-500' },
  dashboard: { label: 'Dashboard', plural: 'Dashboards', icon: BarChart3, color: 'text-amber-500' },
  sheets: { label: 'Sheets', plural: 'Sheets', icon: Table2, color: 'text-teal-500' },
  'native-ppt': { label: 'Native PPT', plural: 'Native PPTs', icon: Presentation, color: 'text-orange-500' },
  analysis: { label: 'Analysis', plural: 'Analyses', icon: Search, color: 'text-gray-500' },
};

const PAGE_SIZE = 24;

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB');
}

export function RecentsPage({ projects = [], isLoading = false, onProjectSelect, onDeleteProject, onRenameProject, onCreateNew, userId = null, onLeft }) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renamingId, setRenamingId] = useState(null); // r149
  const [renameValue, setRenameValue] = useState('');
  const [shareProject, setShareProject] = useState(null);
  const [confirmLeaveId, setConfirmLeaveId] = useState(null);
  const [leavingId, setLeavingId] = useState(null);

  // Leaving a shared project = finding our own member row, then deleting it.
  const handleLeave = async (projectId) => {
    if (!userId || leavingId) return;
    setLeavingId(projectId);
    try {
      const res = await fetch(withBackend(`/api/projects/${projectId}/members?user_id=${userId}`));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load members');
      const own = (data.members || []).find(m => m.user_id === userId);
      if (!own) throw new Error('Could not find your membership on this project');
      const delRes = await fetch(withBackend(`/api/projects/${projectId}/members/${own.id}?user_id=${userId}`), { method: 'DELETE' });
      const delData = await delRes.json().catch(() => ({}));
      if (!delRes.ok) throw new Error(delData.error || 'Failed to leave project');
      toast.success('You left this shared project');
      onLeft?.(projectId);
    } catch (err) {
      toast.error(err.message || 'Failed to leave project');
    } finally {
      setLeavingId(null);
    }
  };

  // Only offer filter chips for types the user actually has.
  const presentTypes = useMemo(() => {
    const seen = new Set();
    for (const p of projects) {
      const key = p.type === 'presentation' ? 'create-ppt' : p.type;
      if (TYPE_META[key]) seen.add(key);
    }
    return Object.keys(TYPE_META).filter(t => t !== 'presentation' && seen.has(t));
  }, [projects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter(p => {
      const key = p.type === 'presentation' ? 'create-ppt' : p.type;
      if (typeFilter !== 'all' && key !== typeFilter) return false;
      if (q && !(p.name || '').toLowerCase().includes(q) && !(p.client || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [projects, query, typeFilter]);

  const visible = filtered.slice(0, visibleCount);

  if (isLoading) {
    return (
      <div className="p-8 min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 min-h-screen">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Recents</h1>
          <p className="text-sm text-muted-foreground mt-1">Everything you've created, newest first.</p>
        </div>

        {/* Search + type filters */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setVisibleCount(PAGE_SIZE); }}
              placeholder="Search by name or client..."
              className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:border-accent/50 focus:outline-none focus:ring-1 focus:ring-accent/30"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {['all', ...presentTypes].map(t => (
              <button
                key={t}
                onClick={() => { setTypeFilter(t); setVisibleCount(PAGE_SIZE); }}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-200 ${
                  typeFilter === t
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border/70 bg-card text-muted-foreground hover:text-foreground hover:border-accent/40'
                }`}
              >
                {t === 'all' ? 'All' : TYPE_META[t].plural}
              </button>
            ))}
          </div>
        </div>

        {/* Empty states */}
        {projects.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <Clock className="w-6 h-6" />
            </div>
            <p className="text-sm font-medium text-foreground">Nothing here yet</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">Your presentations, documents, and dashboards will appear here.</p>
            {onCreateNew && (
              <Button size="sm" onClick={onCreateNew}>
                <Plus className="w-4 h-4 mr-1.5" />
                Create your first project
              </Button>
            )}
          </div>
        )}
        {projects.length > 0 && filtered.length === 0 && (
          <div className="py-24 text-center text-sm text-muted-foreground">
            No matches for {query.trim() ? `"${query.trim()}"` : 'this filter'}.
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map(p => {
            const key = p.type === 'presentation' ? 'create-ppt' : p.type;
            const meta = TYPE_META[key] || TYPE_META.analysis;
            const TypeIcon = meta.icon;
            const date = formatDate(p.lastAnalyzed);
            return (
              <div
                key={p.id}
                className="group relative rounded-xl border border-border bg-card overflow-hidden cursor-pointer hover:border-accent/40 hover:shadow-md transition-all duration-200"
                onClick={() => onProjectSelect?.(p.id)}
              >
                <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <TypeIcon className={`w-8 h-8 ${meta.color} opacity-50`} />
                  )}
                </div>
                {p.is_shared === true && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
                    <Users className="w-3 h-3" />
                    Shared
                  </span>
                )}
                <div className="p-3">
                  {renamingId === p.id ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const v = renameValue.trim();
                          setRenamingId(null);
                          if (v && v !== p.name) onRenameProject?.(p.id, v);
                        } else if (e.key === 'Escape') {
                          setRenamingId(null);
                        }
                      }}
                      onBlur={() => {
                        const v = renameValue.trim();
                        setRenamingId(null);
                        if (v && v !== p.name) onRenameProject?.(p.id, v);
                      }}
                      className="w-full text-sm font-medium bg-background border border-accent/40 rounded px-1.5 py-0.5 focus:outline-none"
                    />
                  ) : (
                    <p className="text-sm font-medium text-foreground truncate" title={p.name || 'Untitled'}>
                      {p.name || 'Untitled'}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <TypeIcon className={`w-3.5 h-3.5 ${meta.color}`} />
                      {meta.label}
                    </span>
                    {(date || (p.is_shared === true && p.shared_by)) && (
                      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70 min-w-0">
                        {p.is_shared === true && p.shared_by && (
                          <span className="truncate" title={`Shared by ${p.shared_by}`}>by {p.shared_by}</span>
                        )}
                        {date && <span className="flex-shrink-0">{date}</span>}
                      </span>
                    )}
                  </div>
                </div>
                {!p.is_shared && (
                  confirmDeleteId === p.id ? (
                    onDeleteProject && (
                      <div
                        className="absolute top-2 right-2 flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 shadow-md"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="text-[11px] text-muted-foreground">Delete?</span>
                        <button
                          onClick={() => { setConfirmDeleteId(null); onDeleteProject(p.id); }}
                          className="text-[11px] font-semibold text-destructive hover:underline"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-[11px] text-muted-foreground hover:underline"
                        >
                          No
                        </button>
                      </div>
                    )
                  ) : (
                    <>
                      {onDeleteProject && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                          className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive hover:border-destructive/40 flex items-center justify-center transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {userId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShareProject(p); }}
                          className={`absolute top-2 ${onDeleteProject ? 'right-10' : 'right-2'} w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-accent hover:border-accent/40 flex items-center justify-center transition-all`}
                          title="Share with your team"
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {onRenameProject && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenamingId(p.id); setRenameValue(p.name || ''); }}
                          className={`absolute top-2 ${onDeleteProject && userId ? 'right-[4.5rem]' : onDeleteProject || userId ? 'right-10' : 'right-2'} w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground hover:border-accent/40 flex items-center justify-center transition-all`}
                          title="Rename"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </>
                  )
                )}
                {p.is_shared === true && userId && (
                  confirmLeaveId === p.id ? (
                    <div
                      className="absolute top-2 right-2 flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 shadow-md"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="text-[11px] text-muted-foreground">Leave?</span>
                      <button
                        onClick={() => { setConfirmLeaveId(null); handleLeave(p.id); }}
                        className="text-[11px] font-semibold text-destructive hover:underline"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setConfirmLeaveId(null)}
                        className="text-[11px] text-muted-foreground hover:underline"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmLeaveId(p.id); }}
                      disabled={leavingId === p.id}
                      className={`absolute top-2 right-2 w-7 h-7 rounded-lg bg-card/90 border border-border text-muted-foreground ${leavingId === p.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} hover:text-destructive hover:border-destructive/40 flex items-center justify-center transition-all`}
                      title="Leave shared project"
                    >
                      {leavingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>

        {/* Load more */}
        {filtered.length > visibleCount && (
          <div className="mt-8 flex justify-center">
            <Button variant="outline" size="sm" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
              Show more ({filtered.length - visibleCount} remaining)
            </Button>
          </div>
        )}
      </motion.div>

      {shareProject && (
        <ProjectShareModal
          project={shareProject}
          userId={userId}
          onClose={() => setShareProject(null)}
        />
      )}
    </div>
  );
}
